/**
 * Demo-credit money model.
 *
 * - The wallet stores integer UNITS. 100 units = 1 demo credit.
 * - Game maths works in "x100" integers: hundredths of the charged stake.
 *   A win of 250 x100 on a stake of 100 credits pays 250 credits.
 * - Conversion from x100 to wallet units always rounds DOWN (floor) to a
 *   whole unit, applied ONCE to the round's total, never per event.
 * - "Payout" is the total amount credited after the stake was debited.
 *   Balance 10,000 cr, stake 100 cr, payout 250 cr => 10,150 cr.
 *
 * JSON transport uses decimal-integer strings for unit amounts.
 */

export const UNITS_PER_CREDIT = 100n;
export const INITIAL_BALANCE_UNITS = 10_000n * UNITS_PER_CREDIT;

/** Stakes a player may choose, in units. Shared by every slot unless a game overrides it. */
export const DEFAULT_BET_LEVELS_UNITS: readonly bigint[] = [
  20n, 40n, 100n, 200n, 500n, 1_000n, 2_000n, 5_000n, 10_000n,
];

/** Hard upper bound for any single amount we move (1e12 units = 10bn credits). */
export const MAX_AMOUNT_UNITS = 1_000_000_000_000n;

export function payoutUnits(stakeUnits: bigint, totalWinX100: bigint | number): bigint {
  const x = typeof totalWinX100 === 'number' ? BigInt(totalWinX100) : totalWinX100;
  if (stakeUnits < 0n || x < 0n) throw new Error('negative amount');
  return (stakeUnits * x) / 100n; // BigInt division truncates == floor for non-negatives
}

export function parseUnits(s: string): bigint {
  if (!/^(0|[1-9][0-9]{0,15})$/.test(s)) throw new Error(`invalid unit amount: ${s}`);
  const v = BigInt(s);
  if (v > MAX_AMOUNT_UNITS) throw new Error('amount out of range');
  return v;
}

export function formatCredits(units: bigint | string, opts: { symbol?: boolean } = {}): string {
  const u = typeof units === 'string' ? BigInt(units) : units;
  const neg = u < 0n;
  const abs = neg ? -u : u;
  const whole = abs / UNITS_PER_CREDIT;
  const frac = abs % UNITS_PER_CREDIT;
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const s = `${neg ? '-' : ''}${wholeStr}.${frac.toString().padStart(2, '0')}`;
  return opts.symbol === false ? s : `${s} DC`;
}

/**
 * Format an amount in the session's currency: DEMO -> "1,234.50 DC", PLAY (play credits, no cash value) ->
 * "1,234.50 credits", an ISO 4217 code -> its symbol, e.g. AUD "$1,234.50", GBP "£1,234.50" (100 units = 1).
 */
export function formatMoney(units: bigint | string, currency: string = 'DEMO', opts: { symbol?: boolean } = {}): string {
  const plain = formatCredits(units, { symbol: false });
  if (opts.symbol === false) return plain;
  if (currency === 'DEMO') return `${plain} DC`;
  if (currency === 'PLAY') return `${plain} credits`;
  let sym: string | null = null;
  try {
    sym = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? null;
  } catch {
    /* not an ISO 4217 code (e.g. a crypto ticker such as BTC or USDT) */
  }
  // no symbol of its own: "1,234.50 USDT"
  if (!sym || sym === currency) return `${plain} ${currency}`;
  return plain.startsWith('-') ? `-${sym}${plain.slice(1)}` : `${sym}${plain}`;
}

/** Display helper for an x100 multiplier, e.g. 250 -> "2.50x". */
export function formatX100(x100: number | bigint): string {
  const v = BigInt(x100);
  const whole = v / 100n;
  const frac = v % 100n;
  return `${whole}.${frac.toString().padStart(2, '0')}x`;
}
